import { ButtonLink } from "@/components/ui/Button";
import type { SatelliteEvent } from "@/interfaces/satellite_event";
import type { ProgramConfig } from "@/interfaces/program";

// The dashboard's job is to answer "what do I do now", so this works that out
// once and says it plainly, instead of leaving someone to infer it from a status
// badge. Everything below it on the page is detail.
function decide(
  events: SatelliteEvent[],
  rsvps: SatelliteEvent[],
  program: ProgramConfig,
) {
  const draft = events.find((e) => e.aasm_state === "draft");
  const rejected = events.find((e) => e.aasm_state === "rejected");
  const submitted = events.find((e) => e.aasm_state === "submitted");
  const approved = events.find((e) => e.aasm_state === "approved");

  if (rejected) {
    return {
      tone: "warning" as const,
      title: "Your application came back with notes",
      body: "Have a read, make the changes, and send it to us again.",
      href: `/organize/${rejected.id}`,
      cta: "Open it",
    };
  }

  if (draft) {
    const missing = draft.missing_fields?.length ?? 0;
    return {
      tone: "action" as const,
      title: "Finish your application",
      body: missing
        ? `Still needs ${draft.missing_fields!.join(", ")}.`
        : "It's ready - send it to us whenever you like.",
      href: `/organize/${draft.id}`,
      cta: missing ? "Finish it" : "Submit it",
    };
  }

  if (approved) {
    const p = approved.checklist_progress;
    const left = p ? p.total - p.done : 0;

    if (!approved.starts_at_short_label) {
      return {
        tone: "action" as const,
        title: "Add your venue and date",
        body: "People can't plan around your event until it has a time and a place.",
        href: `/organize/${approved.id}`,
        cta: "Add them",
      };
    }

    if (left > 0) {
      return {
        tone: "action" as const,
        title: `${left} thing${left === 1 ? "" : "s"} left on your checklist`,
        body: "Work through them whenever - they're ordered so the early ones matter most.",
        href: `/organize/${approved.id}`,
        cta: "Open checklist",
      };
    }

    return {
      tone: "good" as const,
      title: "You're all set",
      body: "Checklist done. Share your event page and get people signed up.",
      href: `/organize/${approved.id}`,
      cta: "Open your event",
    };
  }

  if (submitted) {
    return {
      tone: "neutral" as const,
      title: "We're reading your application",
      body: "Nothing to do - we'll email you as soon as there's a decision.",
      href: `/organize/${submitted.id}`,
      cta: "View it",
    };
  }

  if (rsvps.length > 0) {
    return {
      tone: "good" as const,
      title: `You're going to ${rsvps[0].name}`,
      body: "Bring a laptop and a charger. That's genuinely it.",
      href: `/events/${rsvps[0].id}`,
      cta: "Event details",
    };
  }

  if (program.organizer_applications_open) {
    return {
      tone: "action" as const,
      title: "Run a Boba Bash in your city",
      body: "Tell us where you are and roughly what you're planning. About five minutes, and you don't need a venue yet.",
      href: "/organize/new",
      cta: "Apply",
    };
  }

  return {
    tone: "neutral" as const,
    title: "Find one near you",
    body: "Boba Bashes are happening all over the world in the same week.",
    href: "/events",
    cta: "Browse events",
  };
}

const TONES = {
  action: "bg-goldenrod",
  good: "bg-leaf/40",
  warning: "bg-sunset-orange/30",
  neutral: "bg-surface",
} as const;

export default function NextStep({
  events,
  rsvps,
  program,
}: {
  events: SatelliteEvent[];
  rsvps: SatelliteEvent[];
  program: ProgramConfig;
}) {
  const step = decide(events, rsvps, program);

  return (
    <div
      className={`border-line shadow-card flex flex-wrap items-center gap-4 border-2 p-5 ${TONES[step.tone]}`}
    >
      <div className="min-w-56 flex-1">
        <h2 className="font-display text-heading text-ink font-bold">
          {step.title}
        </h2>
        <p className="text-body text-ink-muted mt-1">{step.body}</p>
      </div>

      <ButtonLink href={step.href}>{step.cta}</ButtonLink>
    </div>
  );
}
